import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const RESULTS_LATEST_API_URL = "/api/results/latest";
const DISPATCH_CONTEXT_SESSION_STORAGE_KEY = "grain:dispatch-results-context";
const RESULTS_ROUTE_PATHNAMES = new Set(["/results", "/results/latest"]);
const DISPATCH_PERIOD_DAY = "day";
const DISPATCH_PERIOD_EVENING = "evening";

function normalizeDispatchDate(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function resolveNextDispatchDateValue(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().slice(0, 10) : "";
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return "";
  }
  const parsedDate = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  parsedDate.setUTCDate(parsedDate.getUTCDate() + 1);
  return parsedDate.toISOString().slice(0, 10);
}

function normalizeDispatchMonth(value) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  const match = /^(\d{4})-(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return "";
  }

  const month = Number(match[2]);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return "";
  }

  return `${match[1]}-${match[2]}`;
}

export function normalizeDispatchPeriod(value) {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === DISPATCH_PERIOD_EVENING || normalizedValue === "soir") {
    return DISPATCH_PERIOD_EVENING;
  }

  return DISPATCH_PERIOD_DAY;
}

function resolveDispatchMonth(dispatchDate = "", dispatchMonth = "") {
  const normalizedDispatchMonth = normalizeDispatchMonth(dispatchMonth);
  if (normalizedDispatchMonth) {
    return normalizedDispatchMonth;
  }

  const normalizedDispatchDate = normalizeDispatchDate(dispatchDate);
  const match = /^(\d{4}-\d{2})-\d{2}$/.exec(normalizedDispatchDate);
  return match ? match[1] : "";
}

function getWindowSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return userUiPreferenceStorage("session");
  } catch {
    return null;
  }
}

function toResolvedUrl(url) {
  if (typeof url !== "string" || !url.trim()) {
    return null;
  }

  try {
    return new URL(url, "https://grain.local");
  } catch {
    return null;
  }
}

function toUrlString(resolvedUrl, originalUrl) {
  const hasExplicitScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(originalUrl);
  return hasExplicitScheme
    ? resolvedUrl.toString()
    : `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function isResultsRouteUrl(url) {
  const resolvedUrl = toResolvedUrl(url);
  return Boolean(resolvedUrl && RESULTS_ROUTE_PATHNAMES.has(resolvedUrl.pathname));
}

export function readDispatchDateFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDispatchDate(params.get("dispatch_date"));
}

export function readDispatchMonthFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDispatchMonth(params.get("dispatch_month"));
}

export function readDispatchPeriodFromSearch(search = "") {
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  return normalizeDispatchPeriod(params.get("dispatch_period"));
}

export function applyDispatchPeriodSearchParam(searchParams, dispatchPeriod = DISPATCH_PERIOD_DAY) {
  if (!(searchParams instanceof URLSearchParams)) {
    return searchParams;
  }

  if (normalizeDispatchPeriod(dispatchPeriod) === DISPATCH_PERIOD_EVENING) {
    searchParams.set("dispatch_period", DISPATCH_PERIOD_EVENING);
  } else {
    searchParams.delete("dispatch_period");
  }

  return searchParams;
}

export function readDispatchDateFromUrl(url) {
  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl) {
    return "";
  }

  return readDispatchDateFromSearch(resolvedUrl.search);
}

export function readDispatchMonthFromUrl(url) {
  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl) {
    return "";
  }

  return readDispatchMonthFromSearch(resolvedUrl.search);
}

export function readDispatchPeriodFromUrl(url) {
  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl) {
    return DISPATCH_PERIOD_DAY;
  }

  return readDispatchPeriodFromSearch(resolvedUrl.search);
}

export function readCurrentDispatchDate() {
  if (typeof window === "undefined") {
    return "";
  }

  return readDispatchDateFromSearch(window.location.search || "");
}

export function readCurrentDispatchPeriod() {
  if (typeof window === "undefined") {
    return DISPATCH_PERIOD_DAY;
  }

  return readDispatchPeriodFromSearch(window.location.search || "");
}

export function readCurrentDispatchContext() {
  if (typeof window === "undefined") {
    return {
      dispatch_date: "",
      dispatch_month: "",
      dispatch_period: DISPATCH_PERIOD_DAY,
    };
  }

  const dispatchDate = readDispatchDateFromSearch(window.location.search || "");
  return {
    dispatch_date: dispatchDate,
    dispatch_month: resolveDispatchMonth(dispatchDate, readDispatchMonthFromSearch(window.location.search || "")),
    dispatch_period: readDispatchPeriodFromSearch(window.location.search || ""),
  };
}

export function readLastDispatchContext() {
  const sessionStorage = getWindowSessionStorage();
  if (!sessionStorage) {
    return {
      dispatch_date: "",
      dispatch_month: "",
      dispatch_period: DISPATCH_PERIOD_DAY,
    };
  }

  try {
    const rawValue = sessionStorage.getItem(DISPATCH_CONTEXT_SESSION_STORAGE_KEY);
    if (!rawValue) {
      return {
        dispatch_date: "",
        dispatch_month: "",
        dispatch_period: DISPATCH_PERIOD_DAY,
      };
    }

    const parsedValue = JSON.parse(rawValue);
    const dispatchDate = normalizeDispatchDate(parsedValue?.dispatch_date);
    return {
      dispatch_date: dispatchDate,
      dispatch_month: resolveDispatchMonth(dispatchDate, parsedValue?.dispatch_month),
      dispatch_period: normalizeDispatchPeriod(parsedValue?.dispatch_period),
    };
  } catch {
    return {
      dispatch_date: "",
      dispatch_month: "",
      dispatch_period: DISPATCH_PERIOD_DAY,
    };
  }
}

export function writeLastDispatchContext({ dispatch_date = "", dispatch_month = "", dispatch_period = DISPATCH_PERIOD_DAY } = {}) {
  const nextContext = {
    dispatch_date: normalizeDispatchDate(dispatch_date),
    dispatch_month: resolveDispatchMonth(dispatch_date, dispatch_month),
    dispatch_period: normalizeDispatchPeriod(dispatch_period),
  };
  const sessionStorage = getWindowSessionStorage();

  if (!sessionStorage) {
    return nextContext;
  }

  try {
    if (!nextContext.dispatch_date && !nextContext.dispatch_month && nextContext.dispatch_period !== DISPATCH_PERIOD_EVENING) {
      sessionStorage.removeItem(DISPATCH_CONTEXT_SESSION_STORAGE_KEY);
      return nextContext;
    }

    sessionStorage.setItem(DISPATCH_CONTEXT_SESSION_STORAGE_KEY, JSON.stringify(nextContext));
  } catch {
    return nextContext;
  }

  return nextContext;
}

export function enrichResultsUrlWithStoredDispatchContext(url) {
  if (!isResultsRouteUrl(url)) {
    return url;
  }

  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl) {
    return url;
  }

  const explicitDispatchDate = readDispatchDateFromSearch(resolvedUrl.search);
  if (explicitDispatchDate) {
    return url;
  }

  const explicitDispatchMonth = readDispatchMonthFromSearch(resolvedUrl.search);
  const explicitDispatchPeriod = resolvedUrl.searchParams.has("dispatch_period")
    ? readDispatchPeriodFromSearch(resolvedUrl.search)
    : "";
  const storedContext = readLastDispatchContext();
  const restoredDispatchDate = storedContext.dispatch_date;
  const restoredDispatchMonth = explicitDispatchMonth || storedContext.dispatch_month;
  const restoredDispatchPeriod = explicitDispatchPeriod || storedContext.dispatch_period;

  if (!restoredDispatchDate && !restoredDispatchMonth && restoredDispatchPeriod !== DISPATCH_PERIOD_EVENING) {
    return url;
  }

  if (restoredDispatchDate) {
    resolvedUrl.searchParams.set("dispatch_date", restoredDispatchDate);
  }

  if (!explicitDispatchMonth && restoredDispatchMonth) {
    resolvedUrl.searchParams.set("dispatch_month", restoredDispatchMonth);
  }

  if (!explicitDispatchPeriod && restoredDispatchPeriod === DISPATCH_PERIOD_EVENING) {
    resolvedUrl.searchParams.set("dispatch_period", DISPATCH_PERIOD_EVENING);
  }

  return toUrlString(resolvedUrl, url);
}

export function appendDispatchDateToUrl(url, dispatchDate = readCurrentDispatchDate(), dispatchPeriod = readCurrentDispatchPeriod()) {
  if (typeof url !== "string" || !url.trim()) {
    return url;
  }

  const normalizedDispatchDate = normalizeDispatchDate(dispatchDate);
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
  if (!normalizedDispatchDate && normalizedDispatchPeriod !== DISPATCH_PERIOD_EVENING) {
    return url;
  }

  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl) {
    return url;
  }

  if (normalizedDispatchDate) {
    resolvedUrl.searchParams.set("dispatch_date", normalizedDispatchDate);
  }

  if (normalizedDispatchPeriod === DISPATCH_PERIOD_EVENING) {
    resolvedUrl.searchParams.set("dispatch_period", DISPATCH_PERIOD_EVENING);
  } else {
    resolvedUrl.searchParams.delete("dispatch_period");
  }

  return toUrlString(resolvedUrl, url);
}

export function buildScopedResultsLatestApiUrl(dispatchDate = readCurrentDispatchDate(), dispatchPeriod = readCurrentDispatchPeriod()) {
  return appendDispatchDateToUrl(RESULTS_LATEST_API_URL, dispatchDate, dispatchPeriod);
}

export function buildResultsLatestApiInvalidationUrls({ currentSearch = "", resultUrl = "" } = {}) {
  const urls = [RESULTS_LATEST_API_URL];
  const dispatchDate = readDispatchDateFromUrl(resultUrl)
    || readDispatchDateFromSearch(currentSearch)
    || readCurrentDispatchDate();
  const resultUrlForPeriod = toResolvedUrl(resultUrl);
  const resultDispatchPeriod = resultUrlForPeriod?.searchParams?.has("dispatch_period")
    ? readDispatchPeriodFromSearch(resultUrlForPeriod.search)
    : "";
  const currentSearchParams = new URLSearchParams(typeof currentSearch === "string" ? currentSearch : "");
  const currentDispatchPeriod = currentSearchParams.has("dispatch_period")
    ? readDispatchPeriodFromSearch(currentSearch)
    : "";
  const dispatchPeriod = resultDispatchPeriod || currentDispatchPeriod || readCurrentDispatchPeriod();
  const scopedResultsLatestApiUrl = buildScopedResultsLatestApiUrl(dispatchDate, dispatchPeriod);

  if (
    typeof scopedResultsLatestApiUrl === "string"
    && scopedResultsLatestApiUrl
    && scopedResultsLatestApiUrl !== RESULTS_LATEST_API_URL
  ) {
    urls.push(scopedResultsLatestApiUrl);
  }

  return [...new Set(urls)];
}
